import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { Plus, Trash2, LogOut, FileDown, Pencil, Eye, EyeOff, Banknote, UserPlus, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "", phone: "", email: "", password: "", id_proof_type: "Aadhaar", id_proof_number: "",
  property_id: "", room_id: "", plot_number: "", rent_amount: 0, deposit_amount: 0,
  check_in_date: new Date().toISOString().slice(0, 10),
};

function TenantForm({ form, setForm, editId, showPw, setShowPw, props, rooms, onSubmit, onCancel }) {
  const filteredRooms = rooms.filter((r) => r.property_id === form.property_id);
  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 p-6 grid md:grid-cols-3 gap-4" data-testid="tenant-form">
      <div className="md:col-span-3 flex items-center justify-between">
        <div className="font-display text-xl font-bold">{editId ? "Edit tenant" : "Add tenant"}</div>
        {editId && <div className="text-xs text-slate-500">Password changes via dedicated action</div>}
      </div>
      <input required placeholder="Full name" data-testid="tenant-name" className="border border-slate-300 px-4 py-2.5"
             value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input required placeholder="Phone" data-testid="tenant-phone" className="border border-slate-300 px-4 py-2.5"
             value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <input type="email" placeholder="Email (optional)" className="border border-slate-300 px-4 py-2.5"
             value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <div className="relative">
        <input type={showPw ? "text" : "password"} placeholder="Portal password (optional)" data-testid="tenant-password"
               disabled={!!editId}
               className="w-full border border-slate-300 px-4 py-2.5 pr-11 disabled:bg-slate-100 disabled:text-slate-400"
               value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <button type="button" onClick={() => setShowPw(!showPw)}
                data-testid="tenant-form-toggle-password"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-slate-400 hover:text-slate-700">
          {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <select className="border border-slate-300 px-4 py-2.5" value={form.id_proof_type}
              onChange={(e) => setForm({ ...form, id_proof_type: e.target.value })}>
        {["Aadhaar", "PAN", "Passport", "Driving License"].map((x) => <option key={x}>{x}</option>)}
      </select>
      <input placeholder="ID proof number" className="border border-slate-300 px-4 py-2.5"
             value={form.id_proof_number} onChange={(e) => setForm({ ...form, id_proof_number: e.target.value })} />
      <input type="date" required data-testid="tenant-checkin" className="border border-slate-300 px-4 py-2.5"
             value={form.check_in_date} onChange={(e) => setForm({ ...form, check_in_date: e.target.value })} />
      <select required className="border border-slate-300 px-4 py-2.5" value={form.property_id} data-testid="tenant-property"
              onChange={(e) => setForm({ ...form, property_id: e.target.value, room_id: "" })}>
        <option value="">Select property…</option>
        {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <select required className="border border-slate-300 px-4 py-2.5" value={form.room_id} data-testid="tenant-room"
              onChange={(e) => setForm({ ...form, room_id: e.target.value })}>
        <option value="">Select room…</option>
        {filteredRooms.map((r) => <option key={r.id} value={r.id}>{r.room_number} ({r.occupied}/{r.capacity})</option>)}
      </select>
      <input placeholder="Plot / Door No. (optional)" data-testid="tenant-plot"
             className="border border-slate-300 px-4 py-2.5"
             value={form.plot_number} onChange={(e) => setForm({ ...form, plot_number: e.target.value })} />
      <input type="number" min="0" required placeholder="Monthly rent ₹" data-testid="tenant-rent"
             className="border border-slate-300 px-4 py-2.5"
             value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} />
      <input type="number" min="0" placeholder="Deposit ₹" className="border border-slate-300 px-4 py-2.5"
             value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} />
      <div className="md:col-span-3 flex gap-3">
        <button data-testid="tenant-save" className="bg-slate-900 text-white px-6 py-2.5">{editId ? "Update" : "Save"}</button>
        <button type="button" onClick={onCancel} className="bg-slate-100 px-6 py-2.5">Cancel</button>
      </div>
    </form>
  );
}

function AssignRoomForm({ appId, assignForm, setAssignForm, props, onSave, onCancel }) {
  return (
    <div className="mt-4 grid md:grid-cols-5 gap-2 bg-white p-3 border border-amber-200">
      <select className="border border-slate-300 px-3 py-2 text-sm" data-testid="assign-property"
              value={assignForm.property_id}
              onChange={(e) => setAssignForm({ ...assignForm, property_id: e.target.value })}>
        <option value="">Property…</option>
        {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <input placeholder="Room no (e.g. 101)" data-testid="assign-room"
             className="border border-slate-300 px-3 py-2 text-sm"
             value={assignForm.room_number}
             onChange={(e) => setAssignForm({ ...assignForm, room_number: e.target.value })} />
      <input type="number" min="0" placeholder="Rent ₹" data-testid="assign-rent"
             className="border border-slate-300 px-3 py-2 text-sm"
             value={assignForm.rent_amount}
             onChange={(e) => setAssignForm({ ...assignForm, rent_amount: e.target.value })} />
      <input type="date" data-testid="assign-checkin"
             className="border border-slate-300 px-3 py-2 text-sm"
             value={assignForm.check_in_date}
             onChange={(e) => setAssignForm({ ...assignForm, check_in_date: e.target.value })} />
      <div className="flex gap-2">
        <button onClick={() => onSave(appId)} data-testid={`assign-save-${appId}`}
                className="bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800">Save</button>
        <button onClick={onCancel}
                className="bg-slate-100 px-3 py-2 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function PendingSignups({ applications, props, assigning, assignForm, setAssignForm, onStartAssign, onSubmitAssign, onReject }) {
  if (applications.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200" data-testid="pending-signups">
      <div className="px-5 py-4 border-b border-amber-200 flex items-center gap-2">
        <UserPlus className="h-5 w-5 text-amber-600" />
        <div className="font-display font-bold text-lg">Pending signups</div>
        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 ml-1">{applications.length}</span>
      </div>
      <ul className="divide-y divide-amber-200">
        {applications.map((app) => (
          <li key={app.id} className="px-5 py-4" data-testid={`signup-${app.id}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">{app.name}</div>
                <div className="text-xs text-slate-600 mt-0.5">{app.phone}{app.email ? ` · ${app.email}` : ""}</div>
                {app.note && <div className="text-xs text-slate-500 mt-1 italic">"{app.note}"</div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                {assigning !== app.id ? (
                  <>
                    <button onClick={() => onStartAssign(app)} data-testid={`assign-${app.id}`}
                            className="bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" /> Assign room
                    </button>
                    <button onClick={() => onReject(app.id)} data-testid={`reject-${app.id}`}
                            className="bg-white border border-slate-300 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50 inline-flex items-center gap-1">
                      <X className="h-4 w-4" /> Reject
                    </button>
                  </>
                ) : null}
              </div>
            </div>
            {assigning === app.id && (
              <AssignRoomForm appId={app.id} assignForm={assignForm} setAssignForm={setAssignForm}
                              props={props} onSave={onSubmitAssign} onCancel={() => onStartAssign(null)} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PropertyFilter({ props, items, filterProp, setFilterProp, visibleCount, propName }) {
  if (props.length === 0) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4">
      <div className="w-full sm:w-80">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Filter by PG / property</label>
        <select data-testid="tenant-prop-filter" value={filterProp}
                onChange={(e) => setFilterProp(e.target.value)}
                className="w-full border border-slate-300 px-4 py-2.5 bg-white">
          <option value="all">All properties ({items.length} tenants)</option>
          {props.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({items.filter((t) => t.property_id === p.id).length})
            </option>
          ))}
        </select>
      </div>
      {filterProp !== "all" && (
        <div className="text-sm text-slate-500 pb-2" data-testid="tenant-filter-count">
          Showing {visibleCount} tenant{visibleCount === 1 ? "" : "s"} in {propName(filterProp)}
        </div>
      )}
    </div>
  );
}

function TenantTable({ items, totalItems, propName, roomName, onEdit, onMarkRentPaid, onAgreement, onCheckout, onRemove }) {
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      {items.length === 0 ? (
        <div className="p-12 text-center text-slate-500" data-testid="tenants-empty">
          {totalItems === 0 ? "No tenants yet." : "No tenants in this property."}
        </div>
      ) : (
        <table className="w-full min-w-[640px]">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-6 py-3">Name</th><th className="px-6 py-3">Phone</th>
              <th className="px-6 py-3">Property / Room</th><th className="px-6 py-3">Rent</th>
              <th className="px-6 py-3">Status</th><th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-4 font-medium">{t.name}</td>
                <td className="px-6 py-4 font-mono text-sm">{t.phone}</td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {propName(t.property_id)} · {roomName(t.room_id)}
                  {t.plot_number ? <span className="text-slate-400"> · Plot {t.plot_number}</span> : null}
                </td>
                <td className="px-6 py-4 font-mono">₹{Number(t.rent_amount).toLocaleString("en-IN")}</td>
                <td className="px-6 py-4">
                  <span className={`px-2.5 py-0.5 text-xs ${
                    t.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"
                  }`}>{t.status === "active" ? "ACTIVE" : "CHECKED OUT"}</span>
                </td>
                <td className="px-6 py-4 text-right space-x-1">
                  <button onClick={() => onEdit(t)} title="Edit tenant"
                          data-testid={`edit-${t.id}`}
                          className="text-slate-700 hover:bg-slate-100 p-2"><Pencil className="h-4 w-4" /></button>
                  {t.status === "active" && (
                    <button onClick={() => onMarkRentPaid(t)} title="Mark rent paid (cash)"
                            data-testid={`cash-paid-${t.id}`}
                            className="text-green-700 hover:bg-green-50 p-2"><Banknote className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => onAgreement(t.id)} title="Download agreement"
                          data-testid={`agreement-${t.id}`}
                          className="text-blue-700 hover:bg-blue-50 p-2"><FileDown className="h-4 w-4" /></button>
                  {t.status === "active" && (
                    <button onClick={() => onCheckout(t.id)} title="Checkout"
                            data-testid={`checkout-${t.id}`}
                            className="text-orange-600 hover:bg-orange-50 p-2"><LogOut className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => onRemove(t.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Tenants() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [assigning, setAssigning] = useState(null); // application id being assigned
  const [assignForm, setAssignForm] = useState({ property_id: "", room_number: "", rent_amount: 0, deposit_amount: 0, check_in_date: new Date().toISOString().slice(0, 10) });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [filterProp, setFilterProp] = useState("all");
  const [form, setForm] = useState(EMPTY_FORM);

  const resetForm = () => setForm(EMPTY_FORM);

  const startEdit = (t) => {
    setEditId(t.id);
    setForm({
      name: t.name || "", phone: t.phone || "", email: t.email || "", password: "",
      id_proof_type: t.id_proof_type || "Aadhaar",
      id_proof_number: t.id_proof_number || "",
      property_id: t.property_id || "", room_id: t.room_id || "",
      plot_number: t.plot_number || "",
      rent_amount: t.rent_amount ?? 0, deposit_amount: t.deposit_amount ?? 0,
      check_in_date: t.check_in_date || new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const closeForm = () => { setOpen(false); setEditId(null); resetForm(); };

  const load = useCallback(async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/tenants"), api.get("/properties"), api.get("/rooms"),
      api.get("/tenant-applications").catch(() => ({ data: [] })),
    ]);
    setItems(a.data); setProps(b.data); setRooms(c.data); setApplications(d.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        rent_amount: Number(form.rent_amount),
        deposit_amount: Number(form.deposit_amount),
      };
      if (!payload.email) delete payload.email;
      if (!payload.password) delete payload.password;
      if (!payload.plot_number) delete payload.plot_number;
      if (editId) {
        // PATCH: skip password here (admin uses dedicated set-password route)
        delete payload.password;
        await api.patch(`/tenants/${editId}`, payload);
        toast.success("Tenant updated");
      } else {
        await api.post("/tenants", payload);
        toast.success("Tenant added");
      }
      closeForm();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const checkout = async (id) => {
    if (!window.confirm("Mark tenant as checked out?")) return;
    await api.post(`/tenants/${id}/checkout`); toast.success("Checked out"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete tenant?")) return;
    await api.delete(`/tenants/${id}`); toast.success("Deleted"); load();
  };
  const downloadAgreement = (id) => {
    window.open(`${API_BASE}/tenants/${id}/agreement.pdf`, "_blank");
  };
  const markRentPaid = async (t) => {
    if (!window.confirm(`Mark ${t.name}'s rent as PAID (cash collected)?`)) return;
    try {
      const r = await api.post(`/tenants/${t.id}/mark-rent-paid`);
      toast.success(`Rent marked paid — ₹${Number(r.data.amount).toLocaleString("en-IN")} (${r.data.period})`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startAssign = (app) => {
    if (!app) { setAssigning(null); return; }
    setAssigning(app.id);
    setAssignForm({ property_id: "", room_number: "", rent_amount: 0, deposit_amount: 0,
                    check_in_date: new Date().toISOString().slice(0, 10) });
  };
  const submitAssign = async (appId) => {
    if (!assignForm.property_id || !assignForm.room_number?.trim() || !assignForm.rent_amount) {
      toast.error("Pick property, enter room number and set a rent amount"); return;
    }
    try {
      await api.post(`/tenant-applications/${appId}/approve`, {
        ...assignForm,
        room_number: assignForm.room_number.trim(),
        rent_amount: Number(assignForm.rent_amount),
        deposit_amount: Number(assignForm.deposit_amount),
      });
      toast.success("Tenant assigned and activated");
      setAssigning(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const rejectApp = async (appId) => {
    if (!window.confirm("Reject this signup?")) return;
    await api.delete(`/tenant-applications/${appId}`);
    toast.success("Rejected");
    load();
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "—";
  const roomName = (id) => rooms.find((r) => r.id === id)?.room_number || "—";
  const visibleItems = filterProp === "all"
    ? items
    : items.filter((t) => t.property_id === filterProp);

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Manage</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Tenants</h1>
        </div>
        <button onClick={() => { setEditId(null); resetForm(); setOpen(true); }} data-testid="add-tenant-btn"
                disabled={rooms.length === 0}
                className="bg-blue-600 text-white px-5 py-3 hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50 self-start sm:self-auto">
          <Plus className="h-4 w-4" /> Add tenant
        </button>
      </div>

      <PendingSignups applications={applications} props={props}
                      assigning={assigning} assignForm={assignForm} setAssignForm={setAssignForm}
                      onStartAssign={startAssign} onSubmitAssign={submitAssign} onReject={rejectApp} />

      <PropertyFilter props={props} items={items} filterProp={filterProp} setFilterProp={setFilterProp}
                      visibleCount={visibleItems.length} propName={propName} />

      {rooms.length === 0 && <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4">Add a property and room first.</div>}

      {open && (
        <TenantForm form={form} setForm={setForm} editId={editId} showPw={showPw} setShowPw={setShowPw}
                    props={props} rooms={rooms} onSubmit={submit} onCancel={closeForm} />
      )}

      <TenantTable items={visibleItems} totalItems={items.length}
                   propName={propName} roomName={roomName}
                   onEdit={startEdit} onMarkRentPaid={markRentPaid} onAgreement={downloadAgreement}
                   onCheckout={checkout} onRemove={remove} />
    </div>
  );
}
